import { useState, useEffect, useCallback, useRef } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { useQueryClient } from 'react-query';
import { Editor } from 'tldraw';

import { saveVisualisationChanges } from '../syncing-helpers/syncTimelineWithVisualisationChanges';
import { VisualisationChangeHandler } from '../syncing-helpers/visualisationChangeHandler';
import { PendingChange } from '../syncing-helpers/visualisationPendingChanges';

export interface UseTimelineChangesOptions {
  editor: Editor | null;
  caseId: string | null;
  chronologyId: string | null;
}

export interface TimelineChangesState {
  changeCount: number;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  changes: PendingChange[];
}

export const useTimelineChanges = (options: UseTimelineChangesOptions) => {
  const { editor, caseId, chronologyId } = options;
  const queryClient = useQueryClient();
  const authInfo = useAuthInfo();

  if (!caseId || !chronologyId) {
    console.error('Missing required parameters for tracking timeline changes');
    return;
  }

  const [state, setState] = useState<TimelineChangesState>({
    changeCount: 0,
    hasUnsavedChanges: false,
    isSaving: false,
    changes: [],
  });

  const changeHandlerRef = useRef<VisualisationChangeHandler | null>(null);

  // Update state from pending changes
  const updateState = useCallback(() => {
    if (!changeHandlerRef.current) return;

    const pendingChanges = changeHandlerRef.current.getPendingChanges();
    const changes = pendingChanges.getAllChanges();
    const changeCount = pendingChanges.getChangeCount();

    setState((prev) => ({
      ...prev,
      changeCount,
      hasUnsavedChanges: changeCount > 0,
      changes,
    }));
  }, []);

  // Initialize change handler, this handler tracks the changes made to the visual timeline
  useEffect(() => {
    if (!editor || !caseId || !chronologyId) {
      return;
    }

    // Initialize change handler
    if (!changeHandlerRef.current) {
      changeHandlerRef.current = new VisualisationChangeHandler(editor, {
        caseId,
        chronologyId,
        onChangesUpdated: updateState,
      });
    }

    // Initial state update to make sure local state reflects anything on the handler already
    updateState();

    return () => {
      // On component unmount dispose of any changes
      if (changeHandlerRef.current) {
        changeHandlerRef.current.dispose();
        changeHandlerRef.current = null;
      }
    };
  }, [editor, caseId, chronologyId, queryClient, updateState]);

  // Provide method to start tracking after timeline is created
  const startTracking = useCallback(() => {
    if (changeHandlerRef.current) {
      changeHandlerRef.current.startTracking();
    }
  }, []);

  // Save all changes
  const saveAllChanges = useCallback(async () => {
    if (!changeHandlerRef.current || state.isSaving || !state.hasUnsavedChanges) {
      return;
    }

    setState((prev) => ({ ...prev, isSaving: true }));

    try {
      await saveVisualisationChanges({
        caseId,
        chronologyId,
        getAccessToken: authInfo.tokens.getAccessToken,
        queryClient,
        pendingChanges: changeHandlerRef.current.getPendingChanges(),
        onComplete: () => {
          updateState();
        },
      });
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setState((prev) => ({ ...prev, isSaving: false }));
    }
  }, [state.isSaving, state.hasUnsavedChanges, caseId, chronologyId, queryClient, authInfo.tokens, updateState]);

  // Discard all changes
  const discardAllChanges = useCallback(() => {
    if (changeHandlerRef.current) {
      changeHandlerRef.current.clearAllChanges();
    }
    updateState();
  }, [updateState]);

  return {
    // State
    ...state,

    // Actions
    saveAllChanges,
    discardAllChanges,
    startTracking,
  };
};
